import React, { useEffect } from 'react';
import { Field, Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';
import { isStripeError } from '../../util/errors';
import * as validators from '../../util/validators';
import { propTypes } from '../../util/types';
import { profilePageParams } from '../../util/userHelpers';
import { isBusinessUser } from '../../util/userTypes';

import {
  H4,
  Button,
  ExternalLink,
  FieldSelect,
  FieldRadioButton,
  FieldTextInput,
  Form,
} from '../../components';
import { formatNorwegianAccount, isValidNorwegianAccount } from '../../util/norwegianBankAccount';
import { formatDateOfBirth, parseDateOfBirth, isAdultDateOfBirth } from '../../util/dateOfBirth';

import css from './StripeConnectAccountForm.module.css';

// Hidden input field
const FieldHidden = props => {
  const { name } = props;
  return (
    <Field id={name} name={name} type="hidden" className={css.unitTypeHidden}>
      {fieldRenderProps => <input {...fieldRenderProps?.input} />}
    </Field>
  );
};

const CreateStripeAccountFields = props => {
  const routeConfiguration = useRouteConfiguration();
  const {
    disabled,
    countryLabel,
    showAsRequired,
    formApi,
    values,
    intl,
    currentUserId,
    profileParams,
    marketplaceRootURL,
    defaultMCC,
    lockedAccountType,
  } = props;

  /*
  We pass some default values to Stripe when creating a new Stripe account in order to reduce couple of steps from Connect Onboarding form.
  - businessProfileURL: user's profile URL
  - businessProfileMCC: default MCC code from stripe-config.js
  - accountToken (https://stripe.com/docs/connect/account-tokens) with following information:
    * accountType: individual or business
    * tos_shown_and_accepted: true
  Only country and bank account token are mandatory values. If you decide to remove the additional default values listed here, remember to update the `createStripeAccount` function in `ducks/stripeConnectAccount.duck.js`.
  */

  useEffect(() => {
    // Use user profile page as business_url on this marketplace
    // or just fake it if it's dev environment using Stripe test endpoints
    // because Stripe will not allow passing a localhost URL
    const hasBusinessURL = values?.businessProfileURL;
    if (!hasBusinessURL && currentUserId) {
      // Prefer the seller's vanity username for the profile URL we send to
      // Stripe (e.g. /u/navnet-ditt), falling back to the UUID when they
      // don't have one. The ProfilePage `:id` route resolves either.
      const params = profileParams || { id: currentUserId.uuid };
      const pathToProfilePage = createResourceLocatorString(
        'ProfilePage',
        routeConfiguration,
        params,
        {}
      );
      const hasMarketplaceRootURL = !!marketplaceRootURL;
      const rootUrl = hasMarketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : null;
      const profilePageURL =
        hasMarketplaceRootURL && !rootUrl.includes('localhost')
          ? `${rootUrl}${pathToProfilePage}`
          : `https://test-marketplace.com${pathToProfilePage}`;
      const defaultBusinessURL = `${profilePageURL}?mode=storefront`;
      formApi.change('businessProfileURL', defaultBusinessURL);
    }

    const hasMCC = values?.businessProfileMCC;
    // Use default merchant category code (MCC) from stripe-config.js
    if (!hasMCC && defaultMCC) {
      formApi.change('businessProfileMCC', defaultMCC);
    }
  }, []);

  return (
    <div className={css.sectionContainer}>
      <H4 as="h3" className={css.sectionTitle}>
        <FormattedMessage id="StripeConnectAccountForm.accountTypeTitle" />
      </H4>
      {lockedAccountType ? (
        // A business account registers with Stripe as what it is. The value
        // is set with the form's initial values; this only says so.
        <div className={css.accountTypeLocked}>
          <FieldHidden name="accountType" />
          <p className={css.accountTypeLockedValue}>
            <FormattedMessage id={`StripeConnectAccountForm.accountTypes.${lockedAccountType}`} />
          </p>
          <p className={css.accountTypeLockedNote}>
            <FormattedMessage id="StripeConnectAccountForm.accountTypeLockedNote" />
          </p>
        </div>
      ) : (
        <div className={css.radioButtonRow}>
          <FieldRadioButton
            id="individual"
            name="accountType"
            label={intl.formatMessage({
              id: 'StripeConnectAccountForm.individualAccount',
            })}
            value="individual"
            showAsRequired={showAsRequired}
          />
          <FieldRadioButton
            id="company"
            name="accountType"
            label={intl.formatMessage({ id: 'StripeConnectAccountForm.companyAccount' })}
            value="company"
            showAsRequired={showAsRequired}
          />
        </div>
      )}

      <FieldHidden name="businessProfileURL" />
      <FieldHidden name="businessProfileMCC" />

      {/* Norway-only marketplace — payout setup is locked to NO so
          sellers can't pick a country that doesn't match their bank
          account. Render a disabled single-option select so the value
          still submits via FinalForm and the seller can see what
          country their Stripe Connected Account will be created under.
          Mirrors the locked country select on the checkout
          Betalingsdetaljer block (StripePaymentAddress). */}
      <FieldSelect
        id="country"
        name="country"
        disabled
        className={css.selectCountry}
        autoComplete="country"
        label={countryLabel}
      >
        <option value="NO">
          {intl.formatMessage({ id: 'StripeConnectAccountForm.countryNames.NO' })}
        </option>
      </FieldSelect>

      {/* Norwegian bank account — collected on our side so Stripe-hosted
          onboarding can skip the bank step. The field accepts the
          familiar 11-digit format (XXXX XX XXXXX); we tokenise it as
          IBAN in the duck before sending to sdk.stripeAccount.create.
          The live IBAN preview below reassures the seller that the
          number they typed matches what Stripe will see. Only renders
          for Norway today — other Stripe countries fall back to
          collecting bank details on Stripe's hosted page. */}
      {values?.country === 'NO' ? (
        <div className={css.bankAccountField}>
          <FieldTextInput
            id="bankAccountNumber"
            name="bankAccountNumber"
            type="text"
            label={intl.formatMessage({
              id: 'StripeConnectAccountForm.bankAccountNumberLabel',
            })}
            placeholder={intl.formatMessage({
              id: 'StripeConnectAccountForm.bankAccountNumberPlaceholder',
            })}
            inputMode="numeric"
            autoComplete="off"
            maxLength={13}
            parse={formatNorwegianAccount}
            validate={validators.composeValidators(
              validators.required(
                intl.formatMessage({
                  id: 'StripeConnectAccountForm.bankAccountNumberRequired',
                })
              ),
              value =>
                isValidNorwegianAccount(value)
                  ? undefined
                  : intl.formatMessage({
                      id: 'StripeConnectAccountForm.bankAccountNumberInvalid',
                    })
            )}
          />
        </div>
      ) : null}

      {/* Date of birth — collected here so it prefills Stripe's identity
          step (it rides into the same client-side account token as the
          name/address). Individuals only; companies provide the
          representative's DOB on Stripe. Never stored on our side. */}
      {values?.country === 'NO' && values?.accountType === 'individual' ? (
        <div className={css.dateOfBirthField}>
          <FieldTextInput
            id="dateOfBirth"
            name="dateOfBirth"
            type="text"
            label={intl.formatMessage({ id: 'StripeConnectAccountForm.dateOfBirthLabel' })}
            placeholder={intl.formatMessage({
              id: 'StripeConnectAccountForm.dateOfBirthPlaceholder',
            })}
            inputMode="numeric"
            autoComplete="bday"
            maxLength={10}
            parse={formatDateOfBirth}
            validate={validators.composeValidators(
              validators.required(
                intl.formatMessage({ id: 'StripeConnectAccountForm.dateOfBirthRequired' })
              ),
              value => {
                if (!parseDateOfBirth(value)) {
                  return intl.formatMessage({ id: 'StripeConnectAccountForm.dateOfBirthInvalid' });
                }
                if (!isAdultDateOfBirth(value)) {
                  return intl.formatMessage({ id: 'StripeConnectAccountForm.dateOfBirthTooYoung' });
                }
                return undefined;
              }
            )}
          />
        </div>
      ) : null}

      <PayoutsIntroBlock />
    </div>
  );
};

/**
 * "Trygg utbetaling med Stripe" educational block. Extracted so the
 * form can render the same content for both the first-time create
 * state (under the form inputs) and the return-unverified state
 * (alongside the saved-info summary), and so the verified state
 * can omit it entirely. Mirrors the client's June 2026 mockup:
 * heading + Stripe wordmark, explanatory paragraph, "Slik fungerer
 * det:" subhead, three emoji-prefixed steps (kjøper betaler / du
 * får betalt / hvorfor IBAN), and a Stripe-IBAN explanation. The
 * "Dette er ditt vanlige kontonummer..." lead-in line + yellow
 * IBAN box come right after via the parent page's FormSpy children
 * so they hide/show together based on whether an IBAN can be
 * computed.
 */
const PayoutsIntroBlock = () => (
  <div className={css.payoutsIntro}>
    <H4 as="h4" className={css.payoutsIntroHeading}>
      <FormattedMessage id="StripeConnectAccountForm.payoutsIntroHeading" />
      <img
        src="/static/images/payments/stripe.svg"
        alt="Stripe"
        className={css.payoutsIntroHeadingLogo}
      />
    </H4>
    <p className={css.payoutsIntroBody}>
      <FormattedMessage
        id="StripeConnectAccountForm.payoutsIntroBody"
        values={{ strong: chunks => <strong>{chunks}</strong> }}
      />
    </p>
    <p className={css.payoutsHowItWorksHeading}>
      <FormattedMessage id="StripeConnectAccountForm.payoutsHowItWorksHeading" />
    </p>
    <ul className={css.payoutsChecklist}>
      <li className={css.payoutsChecklistItem}>
        <span className={css.payoutsChecklistEmoji} aria-hidden="true">
          💰
        </span>
        <span>
          <strong>
            <FormattedMessage id="StripeConnectAccountForm.payoutsHowItWorksBullet1Label" />
          </strong>{' '}
          <FormattedMessage id="StripeConnectAccountForm.payoutsHowItWorksBullet1Body" />
        </span>
      </li>
      <li className={css.payoutsChecklistItem}>
        <span className={css.payoutsChecklistEmoji} aria-hidden="true">
          🏦
        </span>
        <span>
          <strong>
            <FormattedMessage id="StripeConnectAccountForm.payoutsHowItWorksBullet2Label" />
          </strong>{' '}
          <FormattedMessage id="StripeConnectAccountForm.payoutsHowItWorksBullet2Body" />
        </span>
      </li>
      <li className={css.payoutsChecklistItem}>
        <span className={css.payoutsChecklistEmoji} aria-hidden="true">
          💡
        </span>
        <span>
          <strong>
            <FormattedMessage id="StripeConnectAccountForm.payoutsNextStepLabel" />
          </strong>{' '}
          <FormattedMessage id="StripeConnectAccountForm.payoutsNextStepBody" />
        </span>
      </li>
    </ul>
  </div>
);

const UpdateStripeAccountFields = props => {
  const {
    countryLabel,
    savedCountry,
    accountTypeLabel,
    savedAccountType,
    submitInProgress,
    stripeBankAccountLastDigits,
  } = props;
  return (
    <div className={css.savedInformation}>
      <label className={css.accountInformationTitle}>{accountTypeLabel}</label>
      <div className={css.savedCountry}>
        <FormattedMessage id={`StripeConnectAccountForm.accountTypes.${savedAccountType}`} />
      </div>
      <label className={css.accountInformationTitle}>{countryLabel}</label>
      <div className={css.savedCountry}>
        <FormattedMessage id={`StripeConnectAccountForm.countryNames.${savedCountry}`} />
      </div>

      {!submitInProgress && !!stripeBankAccountLastDigits ? (
        <>
          <label className={css.accountInformationTitle}>
            <FormattedMessage id="StripeConnectAccountForm.bankAccountLabel" />
          </label>

          <div className={css.savedBankAccount}>
            •••••••••••••••••••••••• {stripeBankAccountLastDigits}
          </div>
        </>
      ) : null}
    </div>
  );
};

const ErrorsMaybe = props => {
  const { stripeAccountError, stripeAccountLinkError } = props;

  const errorMessage = isStripeError(stripeAccountError) ? (
    <FormattedMessage
      id="StripeConnectAccountForm.createStripeAccountFailedWithStripeError"
      values={{ stripeMessage: stripeAccountError.apiErrors[0].meta.stripeMessage }}
    />
  ) : stripeAccountError ? (
    <FormattedMessage id="StripeConnectAccountForm.createStripeAccountFailed" />
  ) : isStripeError(stripeAccountLinkError) ? (
    <FormattedMessage
      id="StripeConnectAccountForm.createStripeAccountLinkFailedWithStripeError"
      values={{ stripeMessage: stripeAccountLinkError.apiErrors[0].meta.stripeMessage }}
    />
  ) : stripeAccountLinkError ? (
    <FormattedMessage id="StripeConnectAccountForm.createStripeAccountLinkFailed" />
  ) : null;

  return errorMessage ? <div className={css.error}>{errorMessage}</div> : null;
};

/**
 * A component that renders a Stripe connect account form.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {function} props.onSubmit - The function to call when the form is submitted
 * @param {Object} props.fieldRenderProps - The field render props
 * @param {propTypes.currentUser} props.currentUser - The current user
 * @param {Object} props.stripeAccountError - The Stripe account error
 * @param {boolean} props.disabled - Whether the form is disabled
 * @param {boolean} props.inProgress - Whether the form is in progress
 * @param {boolean} props.ready - Whether the form is ready
 * @param {string} props.savedCountry - The saved country
 * @param {string} props.stripeBankAccountLastDigits - The last digits of the Stripe bank account
 * @param {boolean} props.stripeAccountFetched - Whether the Stripe account data is fetched
 * @param {string} props.submitButtonText - The text for the submit button
 * @param {Object} props.fieldRenderProps - The field render props
 * @returns {JSX.Element}
 */
const StripeConnectAccountForm = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const { onSubmit, ...restOfProps } = props;
  const isUpdate = props.stripeConnected;
  const stripePublishableKey = config.stripe.publishableKey;

  // Pre-select the most common case so casual sellers don't have to
  // touch the country dropdown or the account-type radios at all:
  // privatperson + Norge covers ~all real Zuprize sign-ups today.
  // A business account (the KBM pilot's user type) is a company at
  // Stripe by definition, so for it the choice is made and the radios
  // are not shown. Spread the parent's initialValues last so an
  // explicit override (e.g. from a saved draft) still wins.
  const lockedAccountType = isBusinessUser(props.currentUser) ? 'company' : null;
  const initialValues = {
    accountType: lockedAccountType || 'individual',
    country: 'NO',
    ...(restOfProps.initialValues || {}),
  };

  return (
    <FinalForm
      {...restOfProps}
      initialValues={initialValues}
      onSubmit={values => onSubmit({ ...values, stripePublishableKey }, isUpdate)}
      mutators={{
        ...arrayMutators,
      }}
      render={fieldRenderProps => {
        const {
          rootClassName,
          className,
          children,
          stripeAccountError,
          stripeAccountLinkError,
          disabled,
          handleSubmit,
          inProgress,
          invalid,
          pristine,
          ready,
          savedCountry,
          savedAccountType,
          stripeAccountFetched,
          stripeBankAccountLastDigits,
          submitButtonText,
          hideSubmitButton,
          form: formApi,
          values,
          stripeConnected,
          currentUser,
          authScopes,
        } = fieldRenderProps;

        // Check if current user has limited rights in order to disable the submit button
        const limitedRights = authScopes?.indexOf('user:limited') >= 0;

        const accountDataLoaded = stripeConnected && stripeAccountFetched && savedCountry;
        const submitInProgress = inProgress;
        const submitDisabled =
          // Skip the pristine + accountType checks when the seller
          // already has a Connected Account. UpdateStripeAccountFields
          // has no editable fields, so the form is always "pristine"
          // in that state — without this carve-out the submit button
          // would be permanently disabled on return visits and the
          // seller would have no way to retry the hosted-onboarding
          // redirect after backing out of Stripe earlier.
          (!stripeConnected && pristine) ||
          invalid ||
          disabled ||
          submitInProgress ||
          limitedRights ||
          (!stripeConnected && !values?.accountType);

        const countryLabel = intl.formatMessage({ id: 'StripeConnectAccountForm.countryLabel' });
        const accountTypeLabel = intl.formatMessage({
          id: 'StripeConnectAccountForm.accountTypeTitle',
        });
        const classes = classNames(rootClassName || css.root, className, {
          [css.disabled]: disabled,
        });

        const showAsRequired = pristine;

        const currentUserId = currentUser ? currentUser.id : null;
        // Profile-URL params for Stripe: prefers the vanity username,
        // falls back to the UUID when the seller doesn't have one.
        const profileParams = currentUser ? profilePageParams(currentUser) : null;

        // If the user doesn't have Stripe connected account,
        // show fields for country and bank account.
        // Otherwise, show only possibility the edit bank account
        // because Stripe doesn't allow user to change the country
        const stripeAccountFields = !stripeConnected ? (
          <CreateStripeAccountFields
            disabled={disabled}
            showAsRequired={showAsRequired}
            countryLabel={countryLabel}
            marketplaceRootURL={config.marketplaceRootURL}
            defaultMCC={config.stripe.defaultMCC}
            lockedAccountType={lockedAccountType}
            currentUserId={currentUserId}
            profileParams={profileParams}
            formApi={formApi}
            values={values}
            intl={intl}
          />
        ) : (
          // Connected account: show the read-only account summary only.
          // The educational "Trygg utbetaling" block and the continue
          // button belong to the create flow — once the account exists,
          // the parent page's StripeConnectAccountStatusBox (verification
          // needed / success) drives the next action instead.
          <UpdateStripeAccountFields
            countryLabel={countryLabel}
            savedCountry={savedCountry}
            accountTypeLabel={accountTypeLabel}
            savedAccountType={savedAccountType}
            stripeBankAccountLastDigits={stripeBankAccountLastDigits}
            submitInProgress={submitInProgress}
            formApi={formApi}
            values={values}
          />
        );

        const stripeConnectedAccountTermsLink = (
          <ExternalLink href="https://stripe.com/connect-account/legal" className={css.termsLink}>
            <FormattedMessage id="StripeConnectAccountForm.stripeConnectedAccountTermsLink" />
          </ExternalLink>
        );

        // Add button title for hovering to explain why the button is disabled when user has limited rights
        const submitButtonTitle = limitedRights
          ? intl.formatMessage({ id: 'StripeConnectAccountForm.buttonTitleLimitedAccess' })
          : null;

        // When the seller is about to submit a NO Connect account, surface
        // a heads-up about Stripe's "verify bankkontonummeret" step that
        // appears on the hosted onboarding page after this submit. Stripe
        // shows an input with the IBAN-format placeholder, but Norwegians
        // almost always read "kontonummer" and type the 11-digit form —
        // which doesn't match the IBAN we already submitted, and Stripe
        // returns the "fields didn't match" error before the seller has
        // a chance to act. Pre-empting the misunderstanding by showing
        // the exact IBAN they'll need + a one-click copy button.
        // The submit button (+ ToS text) belongs to the create flow only.
        // Once a connected account exists, the parent page's
        // StripeConnectAccountStatusBox drives the next action — the
        // "Bli verifisert" button when Stripe needs more, or "Endre
        // Stripe-konto" when verified — so a form submit button here would
        // be redundant (and would just bounce the seller back to Stripe).
        const showSubmitButton = !stripeConnected;
        const submitButtonMaybe =
          !hideSubmitButton && showSubmitButton ? (
            <>
              <Button
                type="submit"
                className={css.submitButton}
                inProgress={submitInProgress}
                disabled={submitDisabled}
                ready={ready}
                title={submitButtonTitle}
              >
                {submitButtonText || (
                  <FormattedMessage id="StripeConnectAccountForm.submitButtonText" />
                )}
                {/* Stripe wordmark reinforces "you're about to be
                    redirected to Stripe" on the CTA itself.
                    Reuses the same sizing as the heading lockup
                    via payoutsIntroHeadingLogo, then layers on
                    .submitButtonLogo to absolutely position it
                    at the top-right of the button. The button
                    itself gets position: relative so the logo
                    anchors to the button bounds. */}
                <img
                  src="/static/images/payments/stripe.svg"
                  alt="Stripe"
                  className={classNames(css.payoutsIntroHeadingLogo, css.submitButtonLogo)}
                />
              </Button>

              <p className={css.termsText}>
                <FormattedMessage
                  id="StripeConnectAccountForm.stripeToSText"
                  values={{ stripeConnectedAccountTermsLink }}
                />
              </p>
            </>
          ) : null;

        // If the Stripe publishable key is not set up, don't show the form
        return config.stripe.publishableKey ? (
          <Form className={classes} onSubmit={handleSubmit}>
            {!stripeConnected || accountDataLoaded ? (
              stripeAccountFields
            ) : (
              <div className={css.savedInformation}>
                <FormattedMessage id="StripeConnectAccountForm.loadingStripeAccountData" />
              </div>
            )}

            <ErrorsMaybe
              stripeAccountError={stripeAccountError}
              stripeAccountLinkError={stripeAccountLinkError}
            />

            {children}

            {submitButtonMaybe}
          </Form>
        ) : (
          <div className={css.missingStripeKey}>
            <FormattedMessage id="StripeConnectAccountForm.missingStripeKey" />
          </div>
        );
      }}
    />
  );
};

export default StripeConnectAccountForm;
